import {
    castToDecimal,
    convertBooleanToYesNo,
    getLastFourAccountNumber,
    getSpendAccountCategory,
} from '@fruitful/fruitils';
import { isFruitfulAccount } from '@fruitful/money-map';
import {
    AccountOwnership,
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountUpsertDto,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    type MoneyMapBankAccountDetails,
    type MoneyMapBaseAccountDetails,
    type MoneyMapIMAccountDetails,
    type MoneyMapQuilttAccountDetails,
    MoneyMapRuleGoalType,
    type MoneyMapRuleGroup,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
    NEW_IM_ACCOUNT_OPTION,
} from '@fruitful/types';
import { capitalize, isEqual, omit } from 'lodash';

import {
    type MembershipDetails,
    MembershipType,
} from '../../entities/entityTypes';

const KEYS_TO_STRIP_FOR_UPSERT = ['createdAt', 'updatedAt'];

export const getMoneyMapDefaultName = (
    membershipDetails: MembershipDetails,
): string => {
    const { users } = membershipDetails;
    const names = users.map(
        ({ firstName, lastName }) =>
            `${(firstName ?? '').trim()} ${(lastName ?? '').trim()}`,
    );

    return `${names.join(' and ')}'s Money Map (${new Date().toLocaleString().replace(',', '')})`;
};

export const getAccountOwnershipAndPrimaryUser = (
    membership: MembershipDetails,
): {
    ownership: AccountOwnership;
    primaryUserId?: string;
} => {
    const isSolo =
        membership.membershipType === MembershipType.SOLO ||
        (membership.users?.length ?? 0) <= 1;

    if (isSolo) {
        return {
            ownership: AccountOwnership.INDIVIDUAL,
            primaryUserId: membership.users?.[0]?.id,
        };
    }

    return {
        ownership: AccountOwnership.JOINT,
        primaryUserId: membership.users?.find(
            (user) => user.stripeCustomerId != null,
        )?.id,
    };
};

export const getMoneyMapAccountDisplayName = (
    account: MoneyMapAccountWithAccountDetails,
): string => {
    const { status, type, accountDetails } = account;
    const { institutionName, name, accountNumberMasked } = accountDetails;

    const isProposed = status === MoneyMapAccountStatus.PROPOSED;
    const lastFour =
        accountNumberMasked != null
            ? ` *${getLastFourAccountNumber(accountNumberMasked)}`
            : '';

    if (isProposed) {
        return `New Fruitful ${type === MoneyMapAccountType.INTERNAL_BANKING ? 'Cash' : 'IM'} account - "${name}"`;
    }

    return `${institutionName} ${name}${lastFour}`;
};

export const getMoneyMapAccountDropdownLabel = (
    account: MoneyMapAccountWithAccountDetails,
): string => {
    const displayName = getMoneyMapAccountDisplayName(account);

    if (account.type === MoneyMapAccountType.EXTERNAL_HOLISTIC) {
        const { accountType, holisticCategory } =
            account.accountDetails as MoneyMapQuilttAccountDetails;
        const spendCategory = getSpendAccountCategory(accountType);

        return `[External][${capitalize(holisticCategory)}${spendCategory != null ? ` - ${capitalize(spendCategory)}` : ''}] ${displayName}`;
    } else if (account.type === MoneyMapAccountType.EXTERNAL_BANKING) {
        return `[External][TransferAccount] ${displayName}`;
    } else if (
        account.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT &&
        account.status !== MoneyMapAccountStatus.PROPOSED
    ) {
        return `[External][Invest - Fruitful IM] ${displayName}`;
    }

    return displayName;
};

export const getMoneyMapAccountOwnership = (
    account?: MoneyMapAccountWithAccountDetails,
): AccountOwnership | undefined => {
    if (account?.status === MoneyMapAccountStatus.EXISTS) {
        // For existing accounts, expect joint user relationship to exist
        return (account?.accountDetails as MoneyMapBankAccountDetails)
            ?.jointUserId != null
            ? AccountOwnership.JOINT
            : AccountOwnership.INDIVIDUAL;
    }

    // Otherwise, ownership will be stored under createParams for proposed accounts
    return account?.createParameters?.ownership;
};

const isPersistedMoneyMapAccountId = (id?: string | null): id is string =>
    id != null &&
    id !== '' &&
    id !== NEW_CASH_ACCOUNT_OPTION.value &&
    id !== NEW_IM_ACCOUNT_OPTION.value &&
    id !== NEW_APEX_IM_ACCOUNT_OPTION.value;

const nestedAccountIsNewProposal = (
    account?: DeepPartial<MoneyMapAccountUpsertDto>,
): boolean => account != null && !isPersistedMoneyMapAccountId(account.id);

// ENG-7083: true when this account object is the one already on the map and
// this save did not change it. The Money Mapper was sending that object back
// on unrelated saves (adding a goal, moving the spend slider). The API treats
// an account object as a proposal and created a new Core or Spend.
const isRoundTrippedMoneyMapAccount = (
    account: DeepPartial<MoneyMapAccountUpsertDto> | undefined,
    cached: DeepPartial<MoneyMapAccountUpsertDto> | undefined,
): boolean => {
    if (account == null || !isPersistedMoneyMapAccountId(account.id)) {
        return false;
    }
    if (
        nestedAccountIsNewProposal(account.moneyMapAccountForTransfer) ||
        nestedAccountIsNewProposal(account.backdoorRothLinkedMoneyMapAccount)
    ) {
        return false;
    }
    if (cached?.id !== account.id) {
        return false;
    }

    // Same account id is not enough: a proposed-account edit keeps the id and
    // changes these fields, and that edit still has to be sent.
    return (
        isEqual(
            account.createParameters ?? null,
            cached.createParameters ?? null,
        ) &&
        (account.moneyMapAccountIdForTransfer ?? null) ===
            (cached.moneyMapAccountIdForTransfer ?? null) &&
        (account.backdoorRothLinkedMoneyMapAccountId ?? null) ===
            (cached.backdoorRothLinkedMoneyMapAccountId ?? null)
    );
};

const omitRoundTrippedAccountFromRule = (
    rule: DeepPartial<MoneyMapRuleWithAccount>,
    cachedRules: Array<DeepPartial<MoneyMapRuleWithAccount>>,
): DeepPartial<MoneyMapRuleWithAccount> => {
    const cached = cachedRules.find(
        (cachedRule) => cachedRule.id != null && cachedRule.id === rule.id,
    );
    if (
        !isRoundTrippedMoneyMapAccount(
            rule.moneyMapAccount,
            cached?.moneyMapAccount,
        )
    ) {
        return rule;
    }

    return {
        ...omit(rule, 'moneyMapAccount'),
        moneyMapAccountId: rule.moneyMapAccountId ?? rule.moneyMapAccount?.id,
    };
};

// ENG-7083: drop fetched account objects this save did not change, and send
// the account id instead. Keep the object when the account is new or its
// proposal fields changed. The money map editor already omits unchanged
// account objects in each form before save.
export const omitRoundTrippedMoneyMapAccounts = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
    cachedMoneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
): DeepPartial<MoneyMapWithRulesAndAccounts> => {
    const cachedRules = cachedMoneyMap.moneyMapRules ?? [];
    const income = moneyMap.incomeMoneyMapAccount;
    const incomeIsRoundTrip = isRoundTrippedMoneyMapAccount(
        income,
        cachedMoneyMap.incomeMoneyMapAccount,
    );

    return {
        ...moneyMap,
        incomeMoneyMapAccount: incomeIsRoundTrip ? undefined : income,
        incomeMoneyMapAccountId: incomeIsRoundTrip
            ? (moneyMap.incomeMoneyMapAccountId ?? income?.id)
            : moneyMap.incomeMoneyMapAccountId,
        moneyMapRules: moneyMap.moneyMapRules?.map((rule) =>
            omitRoundTrippedAccountFromRule(rule, cachedRules),
        ),
        moneyMapRuleGroups: moneyMap.moneyMapRuleGroups?.map((group) => ({
            ...group,
            moneyMapRules: group.moneyMapRules?.map((rule) =>
                omitRoundTrippedAccountFromRule(rule, cachedRules),
            ),
        })),
    };
};

// Remove createdAt and updatedAt from MoneyMap entities to be valid for upsert API calls, and ensure decimal values are parsed to floats
export const cleanAttributesForUpsert = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
): DeepPartial<MoneyMapWithRulesAndAccounts> => {
    const {
        moneyMapRules: rules,
        moneyMapRuleGroups: groups,
        incomeMoneyMapAccount,
    } = moneyMap;

    const stripAccount = (
        account?: DeepPartial<MoneyMapAccountUpsertDto>,
    ): DeepPartial<MoneyMapAccountUpsertDto> | undefined => {
        if (account == null) {
            return undefined;
        }

        const nestedTransfer = account.moneyMapAccountForTransfer;
        const nestedBackdoorRoth = account.backdoorRothLinkedMoneyMapAccount;

        const strippedTransfer =
            nestedTransfer != null ? stripAccount(nestedTransfer) : undefined;
        const strippedBackdoorRoth =
            nestedBackdoorRoth != null
                ? stripAccount(nestedBackdoorRoth)
                : undefined;

        let next: DeepPartial<MoneyMapAccountUpsertDto> = {
            ...account,
            moneyMapAccountForTransfer: strippedTransfer,
            moneyMapAccountIdForTransfer:
                strippedTransfer != null
                    ? undefined
                    : account.moneyMapAccountIdForTransfer,
            backdoorRothLinkedMoneyMapAccount: strippedBackdoorRoth,
            backdoorRothLinkedMoneyMapAccountId:
                strippedBackdoorRoth != null
                    ? undefined
                    : account.backdoorRothLinkedMoneyMapAccountId,
        };

        // Remove `id` if it's a newly proposed account.
        if (
            next.id === NEW_CASH_ACCOUNT_OPTION.value ||
            next.id === NEW_IM_ACCOUNT_OPTION.value ||
            next.id === NEW_APEX_IM_ACCOUNT_OPTION.value
        ) {
            next = omit(next, 'id');
        }

        return omit(
            next,
            'accountDetails', // Make sure to omit accountDetails if they were decorated on the rules for UI purposes
            'externalAccount',
            ...KEYS_TO_STRIP_FOR_UPSERT,
        );
    };

    const stripRule = (rule: DeepPartial<MoneyMapRuleWithAccount>) => {
        const { moneyMapAccount, moneyMapAccountId, percentage } = rule;
        const strippedMoneyMapAccount = stripAccount(moneyMapAccount);
        return omit(
            {
                ...rule,
                // API only expects either an account object to upsert, or just an account id
                // If the account object exists on the rule, use that and remove the id (or vice versa)
                moneyMapAccount: strippedMoneyMapAccount,
                moneyMapAccountId:
                    strippedMoneyMapAccount != null
                        ? undefined
                        : moneyMapAccountId,
                percentage:
                    percentage != null ? castToDecimal(percentage) : undefined,
            },
            ...KEYS_TO_STRIP_FOR_UPSERT,
        );
    };

    const strippedRules = rules != null ? rules.map(stripRule) : undefined;

    const strippedIncomeMoneyMapAccount = stripAccount(incomeMoneyMapAccount);

    const stripGroup = (group: DeepPartial<MoneyMapRuleGroup>) => {
        return omit(
            {
                ...group,
                percentage: castToDecimal(group.percentage as number),
                moneyMapRules:
                    group.moneyMapRules != null
                        ? group.moneyMapRules.map(stripRule)
                        : undefined,
            },
            ...KEYS_TO_STRIP_FOR_UPSERT,
        );
    };

    const strippedGroups = groups != null ? groups.map(stripGroup) : undefined;

    return omit(
        {
            ...moneyMap,
            incomeMoneyMapAccount: strippedIncomeMoneyMapAccount,
            incomeMoneyMapAccountId:
                strippedIncomeMoneyMapAccount != null
                    ? undefined
                    : moneyMap.incomeMoneyMapAccountId,
            moneyMapRules: strippedRules,
            moneyMapRuleGroups: strippedGroups,
        },
        'isAIGenerated', // Don't persist this value to backend, only used to handle an unsaved generated money map client-side
        // Authorship is set server-side (GUIDE on admin upsert); round-tripping
        // these fails validation on MoneyMapUpsertDto (forbidNonWhitelisted).
        'createdByType',
        'createdByUserId',
        // Mode is server-owned. The upsert DTO accepts it and ignores the
        // client value; omit it so a Grove save does not depend on that.
        'advisoryMode',
        ...KEYS_TO_STRIP_FOR_UPSERT,
    );
};

export const shouldUseTransferAccount = (
    moneyMapAccount?: MoneyMapAccountWithAccountDetails,
    moneyMapAccountId?: string,
    moneyMapIncomeAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>,
) => {
    // If the money map's income account is not with Fruitful, then no transfer accounts should be present on the money map since the money map is not income split eligible.
    if (!isFruitfulAccount(moneyMapIncomeAccount)) {
        return false;
    }

    // Check if the specified account requires a transfer account.
    return accountRequiresTransferAccount(moneyMapAccount, moneyMapAccountId);
};

const accountRequiresTransferAccount = (
    moneyMapAccount?: MoneyMapAccountWithAccountDetails,
    moneyMapAccountId?: string,
) => {
    if (moneyMapAccount == null && moneyMapAccountId == null) {
        return false;
    }

    // Check explicit user selection first - new cash and new Apex accounts don't require transfer accounts
    const isNewCashAccount =
        moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;
    const isNewApexAccount =
        moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;
    if (isNewCashAccount || isNewApexAccount) {
        return false;
    }

    const isLinkedAccount =
        moneyMapAccount?.type === MoneyMapAccountType.EXTERNAL_HOLISTIC;
    const isIMAccount =
        moneyMapAccount?.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT;
    const isNewIMAccount = moneyMapAccountId === NEW_IM_ACCOUNT_OPTION.value;
    // Apex IM accounts DO NOT require transfer account.

    return isLinkedAccount || isIMAccount || isNewIMAccount;
};

/**
 * Debt pay down and build-up-target goals need a positive targetBalanceCents so
 * downstream Income Split rules (minimum-balance / target-based routing) can be created.
 */
export const isGoalRuleMissingPositiveTargetBalance = (
    rule: DeepPartial<
        Pick<
            MoneyMapRuleWithAccount,
            'type' | 'goalType' | 'targetBalanceCents'
        >
    >,
): boolean => {
    if (rule.type !== MoneyMapRuleType.GOAL) {
        return false;
    }
    if (
        rule.goalType !== MoneyMapRuleGoalType.DEBT_PAY_DOWN &&
        rule.goalType !== MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE
    ) {
        return false;
    }
    const raw = rule.targetBalanceCents;
    if (raw == null) {
        return true;
    }
    const cents = typeof raw === 'string' ? Number.parseFloat(raw) : raw;
    return !Number.isFinite(cents) || cents <= 0;
};

export const getDefaultAccountSettings = (
    account?: DeepPartial<MoneyMapAccountWithAccountDetails>,
    isTransferAccountSettings = false,
) => {
    // The isTransferAccount boolean helps to handle the null case for account.
    // When account is null, transfer account default settings should be undefined, but non-transfer account forms should be a populated object with defaults.
    if (isTransferAccountSettings && account == null) {
        return undefined;
    }

    const det = account?.accountDetails;

    return {
        moneyMapAccountId: account?.id ?? '',
        name: det?.name ?? '',
        ownership:
            getMoneyMapAccountOwnership(
                account as MoneyMapAccountWithAccountDetails,
            ) ?? '',
        category:
            (det as MoneyMapBankAccountDetails | undefined)?.holisticCategory ??
            '',
        virtualCard:
            convertBooleanToYesNo(
                (det as MoneyMapBankAccountDetails | undefined)?.hasVirtualCard,
            ) ?? '',
        physicalCard:
            convertBooleanToYesNo(
                (det as MoneyMapBankAccountDetails | undefined)
                    ?.hasPhysicalCard,
            ) ?? '',
        investmentAccountType:
            (det as MoneyMapIMAccountDetails | undefined)?.accountType ?? '',
        apexInvestmentAccountType:
            (det as MoneyMapApexAccountDetails | undefined)?.apexAccountType ??
            '',
        portfolio:
            (det as MoneyMapIMAccountDetails | undefined)?.portfolio ?? '',
        apexPortfolio:
            (det as MoneyMapApexAccountDetails | undefined)?.apexPortfolio ??
            '',
        riskLevel:
            (det as MoneyMapIMAccountDetails | undefined)?.riskLevel ?? '',
        apexRiskLevel:
            (det as MoneyMapApexAccountDetails | undefined)?.apexRiskLevel ??
            '',
        apexTimeHorizon:
            (det as MoneyMapApexAccountDetails | undefined)?.apexTimeHorizon ??
            '',
        primaryUserId:
            (det as MoneyMapBaseAccountDetails | undefined)?.primaryUserId ??
            '',
    };
};

export const isMoneyMapValidForPreviewOrActiveStatus = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
): boolean => {
    const { incomeMoneyMapAccountId, moneyMapRules, moneyMapRuleGroups } =
        moneyMap;

    const ruleOfType = (type: MoneyMapRuleType) =>
        (moneyMapRules ?? []).find((rule) => rule.type === type);

    const billsRule = ruleOfType(MoneyMapRuleType.BILLS);
    const spendRule = ruleOfType(MoneyMapRuleType.SPEND);
    const goalRule = ruleOfType(MoneyMapRuleType.GOAL);

    // Check if all groups have valid percentages (add up to 100%)
    const hasValidGroupPercentages = (moneyMapRuleGroups ?? []).every(
        (group) => {
            const groupRules = (moneyMapRules ?? []).filter(
                (rule) => rule.moneyMapRuleGroupId === group.id,
            );

            const totalPercentage = groupRules.reduce((sum, rule) => {
                const percentage = rule.percentage ?? 0;
                // Convert to number if it's a string
                const numericPercentage =
                    typeof percentage === 'string'
                        ? parseFloat(percentage)
                        : percentage;
                return sum + numericPercentage;
            }, 0);

            // Allow for small floating point precision errors (within 0.01)
            // Empty groups (0%) are invalid - they should add up to 100%
            return Math.abs(totalPercentage - 1.0) < 0.01;
        },
    );

    const hasInvalidGoalTargetBalance = (moneyMapRules ?? []).some(
        isGoalRuleMissingPositiveTargetBalance,
    );

    return (
        incomeMoneyMapAccountId != null &&
        billsRule != null &&
        spendRule != null &&
        goalRule != null &&
        hasValidGroupPercentages &&
        !hasInvalidGoalTargetBalance
    );
};

export const getMoneyMapValidationMessage = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
): string | null => {
    const { incomeMoneyMapAccountId, moneyMapRules, moneyMapRuleGroups } =
        moneyMap;

    const ruleOfType = (type: MoneyMapRuleType) =>
        (moneyMapRules ?? []).find((rule) => rule.type === type);

    const billsRule = ruleOfType(MoneyMapRuleType.BILLS);
    const spendRule = ruleOfType(MoneyMapRuleType.SPEND);
    const goalRule = ruleOfType(MoneyMapRuleType.GOAL);

    // Check basic requirements first
    if (incomeMoneyMapAccountId == null) {
        return 'Income account is required';
    }
    if (billsRule == null) {
        return 'Bills account is required';
    }
    if (spendRule == null) {
        return 'Spend account is required';
    }
    if (goalRule == null) {
        return 'At least one goal is required';
    }

    const invalidGoalTargetRule = (moneyMapRules ?? []).find(
        isGoalRuleMissingPositiveTargetBalance,
    );
    if (invalidGoalTargetRule != null) {
        return invalidGoalTargetRule.goalType ===
            MoneyMapRuleGoalType.DEBT_PAY_DOWN
            ? 'Each debt pay down goal must have a loan balance greater than $0 before publishing or finalizing.'
            : 'Each build target balance goal must have a target balance greater than $0 before publishing or finalizing.';
    }

    // Check if any groups have invalid percentages
    const invalidGroups = (moneyMapRuleGroups ?? []).filter((group) => {
        const groupRules = (moneyMapRules ?? []).filter(
            (rule) => rule.moneyMapRuleGroupId === group.id,
        );

        const totalPercentage = groupRules.reduce((sum, rule) => {
            const percentage = rule.percentage ?? 0;
            // Convert to number if it's a string
            const numericPercentage =
                typeof percentage === 'string'
                    ? parseFloat(percentage)
                    : percentage;
            return sum + numericPercentage;
        }, 0);

        // Allow for small floating point precision errors (within 0.01)
        // Empty groups (0%) are invalid - they should add up to 100%
        return Math.abs(totalPercentage - 1.0) >= 0.01;
    });

    if (invalidGroups.length > 0) {
        return 'Set percentages must add up to 100%';
    }

    return null; // All validations passed
};

export const isMoneyMapEditable = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
) => {
    return (
        moneyMap.status === MoneyMapStatus.DRAFT ||
        moneyMap.status === MoneyMapStatus.PREVIEW
    );
};
